import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarRange, CalendarPlus, ShieldCheck, CreditCard, ArrowRight, Clock, CheckCircle2, XCircle, Car, Landmark, Camera, MapPin, History } from "lucide-react";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—");

const statusPill = (s) =>
  s === "confirmed"
    ? "text-[#047857] border-[#A7F3D0] bg-[#ECFDF5]"
    : s === "cancelled"
      ? "text-[#B91C1C] border-[#FECACA] bg-[#FEF2F2]"
      : "text-[#B45309] border-[#FDE68A] bg-[#FFFBEB]";

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold flex items-center gap-3">
    <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Icon size={18} className="text-primary" /></span>
    {children}
  </h2>
);

const HoursExtend = ({ bookingId, email }) => {
  const [hours, setHours] = useState(2);
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/extend-hours-guest`, { hours, origin_url: window.location.origin, email });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 pt-6 border-t border-border/80">
      <p className="text-sm font-ui font-semibold flex items-center gap-2"><Clock size={15} className="gold-text" /> Just need a few extra hours?</p>
      <p className="mt-1 text-xs text-muted-foreground font-ui">Extend by the hour at $25/hr — no full-day charge, no late fees within the extra time.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select data-testid="manage-hours-select" value={hours} onChange={(e) => setHours(Number(e.target.value))} className="input-dark h-11 rounded-full px-4 text-sm font-ui">
          {Array.from({ length: 11 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>{h} hour{h > 1 ? "s" : ""} — ${h * 25}</option>
          ))}
        </select>
        <Button data-testid="manage-hours-button" onClick={pay} disabled={busy} variant="outline" className="rounded-full font-ui border-amber-300 text-amber-700 hover:bg-amber-50 h-11 px-6">
          {busy ? "Redirecting…" : `Pay $${hours * 25} & extend`}
        </Button>
      </div>
    </div>
  );
};

const WireProof = ({ booking: b, email, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("email", email);
      await api.post(`/bookings/${b.id}/wire-proof`, fd);
      toast.success("Proof uploaded — we'll confirm your wire shortly");
      onUploaded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border/80 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow">
      <SectionTitle icon={Landmark}>Bank wire payment</SectionTitle>
      <p className="mt-3 text-sm text-muted-foreground font-ui leading-relaxed">Wired the money? Send us your transfer receipt so we can confirm your booking faster — snap a photo or upload the PDF.</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <label data-testid="manage-wire-proof-camera" className={`inline-flex items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-11 text-sm font-ui cursor-pointer transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <input data-testid="manage-wire-proof-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} />
          <Camera size={15} className="mr-1.5" /> {busy ? "Uploading…" : "Snap a photo"}
        </label>
        <label data-testid="manage-wire-proof-label" className={`inline-flex items-center rounded-full border border-primary/30 text-primary hover:bg-primary/5 px-6 h-11 text-sm font-ui cursor-pointer transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <input data-testid="manage-wire-proof-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={upload} />
          {busy ? "Uploading…" : "Upload file / PDF"}
        </label>
      </div>
      {(b.wire_proofs || []).length > 0 && (
        <ul className="mt-5 space-y-2 rounded-2xl border border-border/60 bg-[#FAF7F2] p-4">
          {b.wire_proofs.map((p, i) => (
            <li key={p.id} data-testid={`manage-wire-proof-${i}`} className="text-xs font-ui flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/wire-proofs/${p.id}/file?email=${encodeURIComponent(email)}`} target="_blank" rel="noreferrer" className="text-primary hover:underline underline-offset-2 font-medium">{p.original_filename || "proof"}</a>
              <span className="text-muted-foreground">— {String(p.created_at).slice(0, 16).replace("T", " ")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function ManageBooking() {
  useSEO({ title: "Manage Your Booking", description: "View your Easy Drive rental, request a reschedule or pay a balance.", path: "/booking", noindex: true });
  const { bookingId } = useParams();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [sending, setSending] = useState(false);

  const load = async (em) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/manage`, { params: { email: (em ?? email).trim() } });
      setB(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load that booking");
      setB(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (sp.get("email")) load(sp.get("email")); }, []); // eslint-disable-line

  const submitRequest = async () => {
    if (!newStart || !newEnd) return toast.error("Pick your new pickup and return dates");
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/reschedule-request`, { email: email.trim(), new_start: newStart, new_end: newEnd });
      toast.success(data.tier === "free" ? "Reschedule requested — this one is free" : "Reschedule requested — the 20% fee will apply on approval");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the request");
    } finally {
      setSending(false);
    }
  };

  const priorCount = (b?.reschedule_history || []).length;
  const req = b?.reschedule_request;

  return (
    <div data-testid="manage-booking-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 font-ui">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary font-semibold">Guest concierge</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight font-bold">Manage your <span className="italic gold-text">booking</span></h1>
      <p className="mt-3 text-base text-muted-foreground">Looking for another trip? <Link to={`/my-rentals${email ? `?email=${encodeURIComponent(email)}` : ""}`} data-testid="manage-myrentals-link" className="text-primary font-semibold hover:underline underline-offset-4 transition-colors">View all my rentals →</Link></p>

      {!b && (
        <div className="mt-10 bg-card border border-border/80 rounded-3xl p-6 sm:p-10 shadow-sm">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-primary" /></span>
            <div>
              <h2 className="font-display text-xl sm:text-2xl tracking-tight font-medium">Find your rental</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Enter the email used on your booking to view it.</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input data-testid="manage-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark h-12 rounded-full px-5" onKeyDown={(e) => e.key === "Enter" && load()} />
            <Button data-testid="manage-load-button" onClick={() => load()} disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 h-12 shrink-0 transition-colors">
              {loading ? "Loading…" : "View booking"}
            </Button>
          </div>
        </div>
      )}

      {b && (
        <div className="mt-10 space-y-6">
          <div className="bg-gradient-to-br from-card via-card to-[#F7F3EC] border border-border/80 rounded-3xl p-6 sm:p-8 shadow-sm relative overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[11px] font-ui tracking-[0.25em] uppercase text-muted-foreground">Booking <span className="text-foreground font-semibold tracking-normal">{b.booking_ref}</span></p>
              <span data-testid="manage-status" className={`text-[11px] font-ui font-semibold uppercase tracking-wide border rounded-full px-3 py-1 ${statusPill(b.status)}`}>{(b.status || "").replaceAll("_", " ")}</span>
            </div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl tracking-tight font-semibold flex items-center gap-3"><Car size={24} className="gold-text" /> {b.vehicle_name}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-ui">
              <div className="rounded-2xl border border-border/80 bg-card/70 p-5">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><CalendarRange size={12} className="gold-text" /> Pickup</p>
                <p data-testid="manage-pickup-date" className="mt-2 font-semibold text-base">{fmtDate(b.start_date)}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={11} /> {b.pickup_location || "West Bay Street office"}</p>
              </div>
              <div className="rounded-2xl border border-border/80 bg-card/70 p-5">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><CalendarRange size={12} className="gold-text" /> Return</p>
                <p data-testid="manage-return-date" className="mt-2 font-semibold text-base">{fmtDate(b.end_date)}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={11} /> {b.dropoff_location || "Same as pickup"}</p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 text-xs font-ui">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3.5 py-1.5"><ShieldCheck size={13} className="gold-text" /> Deposit: {Number(b.deposit_amount) > 0 ? `${fmtMoney(b.deposit_amount)} ${b.deposit_status === "held" ? "card hold (not a charge)" : (b.deposit_status || "").replaceAll("_", " ")}` : "waived"}</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3.5 py-1.5"><CreditCard size={13} className="gold-text" /> Rental paid: {fmtMoney(b.total)}</span>
            </div>
            {b.outstanding > 0 && (
              <div data-testid="manage-outstanding" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-ui text-amber-800">Balance due: <b className="text-base">{fmtMoney(b.outstanding)}</b></p>
                <Link to={`/pay-charges/${b.id}`}>
                  <Button data-testid="manage-pay-balance" size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-10 px-6 transition-colors">Pay balance <ArrowRight size={14} className="ml-1.5" /></Button>
                </Link>
              </div>
            )}
          </div>

          {b.payment_method === "wire" && b.status !== "cancelled" && (
            <WireProof booking={b} email={email.trim().toLowerCase()} onUploaded={() => load()} />
          )}

          {b.status === "confirmed" && (
            <div className="bg-card border border-border/80 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow">
              <SectionTitle icon={CalendarRange}>Need different dates?</SectionTitle>
              <p className="mt-3 text-sm text-muted-foreground font-ui leading-relaxed">
                Reschedule policy: <b className="text-foreground">first change free</b> up to 24h before pickup · <b className="text-foreground">second change 20% fee</b> · a third cancels the booking. Same {b.days}-day length applies.
                {priorCount > 0 && <span className="block mt-1.5">You've already rescheduled <b className="text-foreground">{priorCount}</b> time{priorCount > 1 ? "s" : ""}.</span>}
              </p>

              {req?.status === "pending" ? (
                <div data-testid="manage-request-pending" className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 p-5 flex items-center gap-3 text-sm font-ui text-sky-800">
                  <Clock size={16} className="shrink-0" /> Request pending: <b>{fmtDate(req.new_start)} → {fmtDate(req.new_end)}</b> — we'll confirm by email &amp; SMS shortly.
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {req?.status === "declined" && (
                    <p data-testid="manage-request-declined" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-ui text-red-700 flex items-center gap-2"><XCircle size={15} className="shrink-0" /> Your last request ({fmtDate(req.new_start)} → {fmtDate(req.new_end)}) couldn't be accommodated — your original dates stand.</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-ui uppercase tracking-widest text-muted-foreground">New pickup date</label>
                      <Input data-testid="manage-new-start" type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)} className="input-dark mt-1.5 h-11 rounded-xl" />
                    </div>
                    <div>
                      <label className="text-[10px] font-ui uppercase tracking-widest text-muted-foreground">New return date</label>
                      <Input data-testid="manage-new-end" type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="input-dark mt-1.5 h-11 rounded-xl" />
                    </div>
                  </div>
                  <Button data-testid="manage-request-submit" onClick={submitRequest} disabled={sending} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11 transition-colors">
                    {sending ? "Sending…" : "Request reschedule"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {b.status === "confirmed" && (
            <div className="bg-card border border-border/80 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow">
              <SectionTitle icon={CalendarPlus}>Extend your rental</SectionTitle>
              <p className="mt-3 text-sm text-muted-foreground font-ui leading-relaxed">
                Keep {b.vehicle_name} longer — pick a new return date, pay the difference securely, and your rental contract renews automatically with the new dates blocked out for you. No late fees, same booking number.
              </p>
              <Link to={`/extend/${b.id}`} onClick={() => localStorage.setItem(`booking_email_${b.id}`, email.trim().toLowerCase())}>
                <Button data-testid="manage-extend-button" className="mt-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11 transition-colors">Extend rental <ArrowRight size={14} className="ml-1.5" /></Button>
              </Link>
              <HoursExtend bookingId={b.id} email={email.trim().toLowerCase()} />
            </div>
          )}

          {(b.reschedule_history || []).length > 0 && (
            <div className="bg-card border border-border/80 rounded-3xl p-6 sm:p-8 shadow-sm">
              <p className="text-[11px] font-ui tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-2"><History size={13} className="gold-text" /> Reschedule history</p>
              <ul className="mt-4 space-y-0 text-sm font-ui border-l-2 border-[#E5DED5] ml-1.5">
                {b.reschedule_history.map((h, i) => (
                  <li key={i} data-testid={`manage-history-${i}`} className="relative pl-5 pb-4 last:pb-0 text-foreground/80">
                    <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#C9A227]" />
                    {h.from} → <b className="text-foreground">{h.to}</b> {h.fee > 0 ? <span className="text-amber-700">· 20% fee {fmtMoney(h.fee)}</span> : <span className="text-emerald-700">· free</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
